import React, { useEffect, useState } from 'react';
import produce from 'immer';
import isEqual from 'lodash/isEqual';
import { isTestEnv } from 'common/report/util/env';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { DropMenuReact } from 'common/components/drop-menu';
import { Directions } from 'common/components/drop-menu/types';
import { Visibility } from 'common/components/base/stackable';
import { ActionEvent, BannerAction, BannerTheme, BannerType } from 'common/components/banner/types';
import { BannerReact } from 'common/components/banner';
import { useTranslation } from 'common/i18n/hooks';
import { TranslationKey } from 'common/i18n';
import { hasFirstClassMetrics } from 'apps/lexicon/src/lexicon-app/metrics/utils';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { withStyles } from 'common/util/react';
import { useLocalStorageManager } from 'common/contexts/react/storage-manager-context';
import { RestrictEntitySaving } from 'common/report/constants';
import { reporter } from 'common/report/error-reporting';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import { getPersistedState, setPersistedState } from './util';

// [METRICSGA] Delete this whole component and everything added in this PR once
// First Class Metrics is GA.

const BETA_LEARN_MORE = "https://mixpanel.com/saved-metrics-hub/";
type BannerOptions = {
  button: TranslationKey;
  ctaText: TranslationKey | null;
  description: TranslationKey;
  title: TranslationKey;
};
const preBetaBannerOptions: BannerOptions = {
  button: "first-class-metrics-info.pre-beta.button",
  ctaText: "first-class-metrics-info.pre-beta.cta",
  description: "first-class-metrics-info.pre-beta.description",
  title: "first-class-metrics-info.pre-beta.title"
};
const betaBannerOptions: BannerOptions = {
  button: "first-class-metrics-info.beta.button",
  ctaText: "first-class-metrics-info.beta.cta",
  description: "first-class-metrics-info.beta.description",
  title: "first-class-metrics-info.beta.title"
};
const MetricsBetaButton = () => {
  var _currentProjectResour;
  if (isTestEnv()) {
    return null;
  }
  const [metricsPopupOpen, setMetricsPopupOpen] = useState(false);
  const {
    t
  } = useTranslation();
  const currentProjectResource = useCurrentProject();
  const localStorageManager = useLocalStorageManager();
  const isBeta = hasFirstClassMetrics(currentProjectResource);
  const persistedState = getPersistedState({
    localStorageManager,
    projectId: currentProjectResource.id
  });
  let bannerOptions: BannerOptions = betaBannerOptions;
  if (!isBeta) {
    bannerOptions = preBetaBannerOptions;
  }

  // If the user hasn't already closed the popup, open it.
  useEffect(() => {
    if ((!persistedState.closedBeta && isBeta || !persistedState.closedPreBeta && !isBeta) && !metricsPopupOpen) {
      setMetricsPopupOpen(true);
    }
  }, [isBeta, metricsPopupOpen, persistedState.closedBeta, persistedState.closedPreBeta]);

  // If the user doesn't have access to behaviors metrics, don't show the button at all.
  const hasBehaviorsMetricsAccess = Boolean((_currentProjectResour = currentProjectResource.get()) === null || _currentProjectResour === void 0 || (_currentProjectResour = _currentProjectResour.value) === null || _currentProjectResour === void 0 ? void 0 : _currentProjectResour.canSaveEntity(RestrictEntitySaving.BehaviorMetrics));
  if (isTestEnv() || !hasBehaviorsMetricsAccess) {
    return null;
  }
  const closePopup = () => {
    setMetricsPopupOpen(false);
    try {
      const updatedState = produce(persistedState, draft => {
        if (isBeta) {
          draft.closedBeta = true;
        } else {
          draft.closedPreBeta = true;
        }
        return draft;
      });
      if (!isEqual(persistedState, updatedState)) {
        setPersistedState({
          localStorageManager,
          updatedState
        });
      }
    } catch (error) {
      reporter.error(error);
    }
  };
  return <div data-sentry-component="MetricsBetaButton" data-sentry-source-file="index.tsx">
      <DropMenuReact className={styles.dropMenu} direction={Directions.UpCenter} experimentalPositioning open={metricsPopupOpen} onChange={ev => "detail" in ev && ev.detail.state === Visibility.Closed && closePopup()} data-sentry-element="DropMenuReact" data-sentry-source-file="index.tsx">
        <BannerReact bodyText={t(bannerOptions.description)} ctas={[...(bannerOptions.ctaText ? [{
        actionName: BannerAction.Help,
        text: t(bannerOptions.ctaText),
        theme: ButtonTheme.Primary
      }] : [])]} headerText={t(bannerOptions.title)} icon='behavior-simple' onAction={(ev: ActionEvent) => {
        const actionName = ev.detail.actionName;
        if (actionName === BannerAction.Dismiss) {
          closePopup();
        } else if (actionName === BannerAction.Help) {
          window.open(BETA_LEARN_MORE, "_blank");
        }
      }} showCloseButton theme={BannerTheme.BaseColumn} type={BannerType.Embedded} data-sentry-element="BannerReact" data-sentry-source-file="index.tsx" />
      </DropMenuReact>

      <ButtonReact theme={ButtonTheme.Tertiary} notFocusable onClick={() => !metricsPopupOpen && setMetricsPopupOpen(true)} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
        {t(bannerOptions.button)}
      </ButtonReact>
    </div>;
};
export const MetricsBetaButtonReact = withStyles(MetricsBetaButton, css);